import { memo } from 'react'
import {
  ActiveElement,
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartEvent,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  Plugin,
  PointElement,
  Tooltip,
} from 'chart.js'
import { Bar, Doughnut, Line } from 'react-chartjs-2'
import { IChatManagementReport } from '../models'
import {
  buildCategoryChart,
  buildDailyRateChart,
  buildDailyVolumeChart,
  buildHourlyChart,
  buildIntentChart,
  buildOutcomeChart,
  buildSparkline,
  formatCount,
  formatPercent,
  intentChartRows,
  PALETTE,
} from '../viewModel'
import type { ChartData } from 'chart.js'
import styles from './ChatbotManagementReport.module.scss'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend)

const GRID = '#e1e0d9'
const AXIS = '#52514e'
const FONT = '12px system-ui, -apple-system, "Segoe UI", sans-serif'

/** Yatay barın ucuna değer yazar ("bar → değer uçta", spec §8.5); yeni bağımlılık eklememek için yerel eklenti. */
export const barEndLabels: Plugin<'bar'> = {
  id: 'barEndLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart
    ctx.save()
    ctx.fillStyle = AXIS
    ctx.font = FONT
    ctx.textBaseline = 'middle'
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      chart.getDatasetMeta(datasetIndex).data.forEach((element, index) => {
        const value = Number(dataset.data[index] ?? 0)
        if (!value) return
        const { x, y } = element.getProps(['x', 'y'], true) as { x: number; y: number }
        ctx.fillText(formatCount(value), x + 6, y)
      })
    })
    ctx.restore()
  },
}

/**
 * Sayfadaki 8 grafik için ortak çizim ayarı: pencere boyutu değişirken her pikselde yeniden çizmek yerine
 * boyutlanma bitince bir kez çizer (resizeDelay) ve açılış/yenileme animasyonunu kısaltır.
 */
const RENDER_TUNING = { resizeDelay: 200, animation: { duration: 300 } } as const

const legendBottom = { position: 'bottom' as const, labels: { color: AXIS, usePointStyle: true } }

const verticalBarOptions: ChartOptions<'bar'> = {
  ...RENDER_TUNING,
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { display: false }, ticks: { color: AXIS } },
    y: { beginAtZero: true, grid: { color: GRID }, border: { display: false }, ticks: { color: AXIS, precision: 0 } },
  },
}

const horizontalBarOptions: ChartOptions<'bar'> = {
  indexAxis: 'y',
  ...RENDER_TUNING,
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: { right: 44 } },
  plugins: { legend: { display: false } },
  scales: {
    x: { beginAtZero: true, grid: { color: GRID }, border: { display: false }, ticks: { color: AXIS, precision: 0 } },
    y: { grid: { display: false }, ticks: { color: AXIS } },
  },
}

const stackedHorizontalOptions: ChartOptions<'bar'> = {
  indexAxis: 'y',
  ...RENDER_TUNING,
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: legendBottom },
  scales: {
    x: {
      stacked: true,
      beginAtZero: true,
      grid: { color: GRID },
      border: { display: false },
      ticks: { color: AXIS, precision: 0 },
    },
    y: { stacked: true, grid: { display: false }, ticks: { color: AXIS } },
  },
}

const rateLineOptions: ChartOptions<'line'> = {
  ...RENDER_TUNING,
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: legendBottom,
    tooltip: {
      callbacks: { label: context => `${context.dataset.label}: ${formatPercent(Number(context.parsed.y))}` },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { color: AXIS } },
    y: {
      min: 0,
      max: 100,
      grid: { color: GRID },
      border: { display: false },
      ticks: { color: AXIS, callback: value => `%${value}` },
    },
  },
}

const doughnutOptions: ChartOptions<'doughnut'> = {
  ...RENDER_TUNING,
  responsive: true,
  maintainAspectRatio: false,
  cutout: '62%',
  plugins: {
    legend: legendBottom,
    tooltip: { callbacks: { label: context => `${context.label}: ${formatCount(Number(context.parsed))}` } },
  },
}

const sparklineOptions: ChartOptions<'line'> = {
  ...RENDER_TUNING,
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { enabled: false } },
  scales: { x: { display: false }, y: { display: false } },
}

/** Tıklanan çubuğun/dilimin/noktanın indeksini verir (detaya inme, spec §8.6). */
export type SelectHandler = (index: number) => void

const selectable = <T extends object>(options: T, onSelect?: SelectHandler): T =>
  onSelect
    ? ({
        ...options,
        onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
          if (elements.length > 0) onSelect(elements[0].index)
        },
        onHover: (event: ChartEvent, elements: ActiveElement[]) => {
          const target = event.native?.target as HTMLElement | null | undefined
          if (target) target.style.cursor = elements.length > 0 ? 'pointer' : 'default'
        },
      } as T)
    : options

interface ReportChartProps {
  report: IChatManagementReport
  onSelect?: SelectHandler
}

export function SwatchLegend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <ul className={styles.legend}>
      {items.map(item => (
        <li key={item.label}>
          <span className={styles.swatch} style={{ backgroundColor: item.color }} aria-hidden='true' />
          {item.label}
        </li>
      ))}
    </ul>
  )
}

export const Sparkline = memo(function Sparkline({
  values,
  color,
  label,
}: {
  values: number[]
  color: string
  label: string
}) {
  if (values.length < 2) return null
  return (
    <div className={styles.sparkline} role='img' aria-label={label}>
      <Line data={buildSparkline(values, color)} options={sparklineOptions} />
    </div>
  )
})

export const OutcomeDistributionChart = memo(function OutcomeDistributionChart({ report, onSelect }: ReportChartProps) {
  return (
    <div className={styles.chartCanvas} role='img' aria-label='Yanıt türü dağılımı, adet'>
      <Bar
        data={buildOutcomeChart(report)}
        options={selectable(horizontalBarOptions, onSelect)}
        plugins={[barEndLabels]}
      />
    </div>
  )
})

export const CategoryDoughnutChart = memo(function CategoryDoughnutChart({ report, onSelect }: ReportChartProps) {
  return (
    <div className={styles.chartCanvas} role='img' aria-label='Performans kategorileri payı'>
      <Doughnut data={buildCategoryChart(report)} options={selectable(doughnutOptions, onSelect)} />
      <div className={styles.doughnutCenter} aria-hidden='true'>
        <strong>{formatPercent(report.Kpis.SuccessRate)}</strong>
        <span>başarı oranı</span>
      </div>
    </div>
  )
})

export const DailyVolumeChart = memo(function DailyVolumeChart({ report, onSelect }: ReportChartProps) {
  return (
    <div className={styles.chartCanvasShort} role='img' aria-label='Günlük soru hacmi'>
      <Bar data={buildDailyVolumeChart(report)} options={selectable(verticalBarOptions, onSelect)} />
    </div>
  )
})

export const DailyRateChart = memo(function DailyRateChart({ report, onSelect }: ReportChartProps) {
  return (
    <div className={styles.chartCanvasShort} role='img' aria-label='Günlük başarı, veri yok ve fallback oranları'>
      <Line data={buildDailyRateChart(report)} options={selectable(rateLineOptions, onSelect)} />
    </div>
  )
})

export const HourlyChart = memo(function HourlyChart({ report, onSelect }: ReportChartProps) {
  return (
    <div className={styles.chartCanvas} role='img' aria-label='Saatlik soru yoğunluğu'>
      <Bar data={buildHourlyChart(report)} options={selectable(verticalBarOptions, onSelect)} />
    </div>
  )
})

export const IntentStackChart = memo(function IntentStackChart({ report, onSelect }: ReportChartProps) {
  const rowCount = intentChartRows(report).length
  return (
    <div
      style={{ position: 'relative', height: `${Math.max(240, rowCount * 34 + 80)}px` }}
      role='img'
      aria-label='Araç bazında başarılı, veri yok ve başarısız sorular'
    >
      <Bar data={buildIntentChart(report)} options={selectable(stackedHorizontalOptions, onSelect)} />
    </div>
  )
})

/** Detay penceresindeki genel amaçlı bar grafik (Task 19). */
export const DrillBarChart = memo(function DrillBarChart({
  data,
  label,
  horizontal = false,
  onSelect,
}: {
  data: ChartData<'bar', number[], string>
  label: string
  horizontal?: boolean
  onSelect?: SelectHandler
}) {
  return (
    <div className={styles.chartCanvasShort} role='img' aria-label={label}>
      <Bar
        data={data}
        options={selectable(horizontal ? horizontalBarOptions : verticalBarOptions, onSelect)}
        plugins={horizontal ? [barEndLabels] : []}
      />
    </div>
  )
})

export const OUTCOME_LEGEND = [
  { label: 'Başarı sayılır', color: PALETTE.success },
  { label: 'Başarı sayılmaz', color: PALETTE.improvement },
  { label: 'Paydada değil', color: PALETTE.noise },
]

export const HOURLY_LEGEND = [
  { label: 'Mesai saatleri (09:00–17:59)', color: PALETTE.success },
  { label: 'Mesai dışı', color: PALETTE.offHours },
]
